<template>
  <base-modal
    ref="modalRef"
    :title="'Confirm Overwrite'"
    :primary-color="Colors.YELLOW"
    :show-row2="false"
    :show-footer="true"
    :auto-focus-primary-action="false"
    :persistent="true"
    :primary-action-text="withShortcut('Confirm', shortcuts.SAVE)"
    :secondary-action-text="withShortcut('Cancel', shortcuts.CANCEL)"
    width="420px"
    @show="onShow"
    @hide="onHide"
    @save="handleConfirm"
    @cancel="handleCancel"
  >
    <div class="tw-p-4">
      <p class="tw-text-sm tw-text-gray-dark">
        Are you sure you want to overwrite the existing file?
      </p>
    </div>
  </base-modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import BaseModal from '@/components/modal/templates/BaseModal.vue';
import { useKeyboardShortcuts } from '@/composables/modal/useKeyboardShortcuts';
import { returnFocusToTrigger } from '@/composables/modal';
import { Colors } from '@/api';

interface Emits {
  (e: 'confirm'): void;
  (e: 'cancel'): void;
}

const emit = defineEmits<Emits>();
const { shortcuts, withShortcut } = useKeyboardShortcuts();

// Type for BaseModal exposed methods
interface BaseModalInstance {
  showPopup: (trigger?: HTMLElement) => void;
  closePopup: () => void;
}

const modalRef = ref<BaseModalInstance>();
const triggerElement = ref<HTMLElement | null>(null);

function handleConfirm() {
  emit('confirm');
  closeDialog();
}

function handleCancel() {
  emit('cancel');
  closeDialog();
}

function onShow() {
  // Modal shown
}

function onHide() {
  returnFocusToTrigger(triggerElement);
}

function showDialog(trigger?: HTMLElement) {
  triggerElement.value = trigger || (document.activeElement as HTMLElement);
  modalRef.value?.showPopup(trigger);
}

function closeDialog() {
  modalRef.value?.closePopup();
}

// showPopup function with proper signature for consistency
function showPopup(trigger?: HTMLElement) {
  return showDialog(trigger);
}

defineExpose({
  showDialog,
  showPopup,
  closeDialog,
  closePopup: closeDialog,
});
</script>
